export const STAGE_STYLE = {
  stage_1_intro: "bg-[#EFF6FF] text-[#1E40AF] border-[#BFDBFE]",
  stage_2_samples: "bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]",
  stage_3_dispatch: "bg-[#F3E8FF] text-[#6B21A8] border-[#E9D5FF]",
  stage_4_order: "bg-[#DCFCE7] text-[#166534] border-[#86EFAC]",
  repeat_purchase: "bg-[#ECFEFF] text-[#155E75] border-[#A5F3FC]",
};

export function stageStyle(key) {
  return STAGE_STYLE[key] || "bg-stone-100 text-stone-700 border-stone-200";
}

const POSITIVE = ["positive_feedback", "order_confirmed", "order_expected", "interested", "agreed_samples"];
const NEGATIVE = ["negative_feedback", "not_interested", "wrong_contact", "price_objection"];
const NEUTRAL = ["neutral_feedback", "asked_more_time", "follow_up_later", "testing_pending"];

export function responseStyle(value) {
  if (POSITIVE.includes(value)) return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (NEGATIVE.includes(value)) return "bg-red-50 text-red-700 border-red-200";
  if (NEUTRAL.includes(value)) return "bg-amber-50 text-amber-700 border-amber-200";
  return "bg-stone-100 text-stone-600 border-stone-200";
}

export function waLink(waNumber, text) {
  return `https://wa.me/${waNumber}?text=${encodeURIComponent(text || "")}`;
}

async function urlToFile(url, name) {
  const res = await fetch(url, { mode: "cors" });
  if (!res.ok) throw new Error("fetch failed");
  const blob = await res.blob();
  const type = blob.type || "image/jpeg";
  const ext = (type.split("/")[1] || "jpg").split("+")[0];
  return new File([blob], `${name}.${ext}`, { type });
}

// Sends the actual image file(s) via the device Share sheet (so a phone can attach
// the real JPEG to WhatsApp). Falls back to downloading the image(s) + copying the
// caption on desktop / unsupported browsers. Returns "shared" | "cancelled" | "downloaded".
export async function shareImages(urls, text) {
  const list = (urls || []).filter(Boolean);
  try {
    if (typeof navigator !== "undefined" && navigator.canShare) {
      const files = [];
      for (let i = 0; i < list.length; i++) {
        try { files.push(await urlToFile(list[i], `product-${i + 1}`)); } catch (e) { console.warn("Could not fetch image for sharing", list[i], e); }
      }
      if (files.length && navigator.canShare({ files })) {
        await navigator.share(text ? { files, text } : { files });
        return "shared";
      }
    }
  } catch (e) {
    if (e && e.name === "AbortError") return "cancelled";
  }
  // Fallback: download each image so the user can attach it manually
  try {
    for (let i = 0; i < list.length; i++) {
      const a = document.createElement("a");
      a.href = list[i];
      a.download = `product-${i + 1}.jpg`;
      a.target = "_blank";
      a.rel = "noreferrer";
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
    if (text) { try { await navigator.clipboard.writeText(text); } catch { /* ignore */ } }
  } catch { /* ignore */ }
  return "downloaded";
}

// Shares a single PDF (product spec sheet) via the device Share sheet, else downloads it.
// Returns "shared" | "cancelled" | "downloaded".
export async function sharePdf(url, name) {
  const filename = `${name || "spec-sheet"}.pdf`;
  try {
    if (typeof navigator !== "undefined" && navigator.canShare) {
      const res = await fetch(url, { mode: "cors" });
      if (res.ok) {
        const blob = await res.blob();
        const file = new File([blob], filename, { type: "application/pdf" });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file] });
          return "shared";
        }
      }
    }
  } catch (e) {
    if (e && e.name === "AbortError") return "cancelled";
    console.warn("PDF share failed, falling back to download", e);
  }
  try {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.target = "_blank";
    a.rel = "noreferrer";
    document.body.appendChild(a);
    a.click();
    a.remove();
  } catch (e) {
    console.warn("PDF download failed", e);
  }
  return "downloaded";
}

// Builds the executive's consolidated Daily Work Report PDF URL and shares it on WhatsApp
// (device share sheet on mobile, download on desktop). Returns "shared" | "cancelled" | "downloaded".
export async function shareDailyReport(dateIso, name) {
  const base = process.env.REACT_APP_BACKEND_URL;
  const url = `${base}/api/daily-reports/me/pdf?date=${dateIso}&v=${Date.now()}`;
  const fname = `PGT_Daily_Report_${(name || "report").replace(/[^A-Za-z0-9]+/g, "_")}_${dateIso}`;
  return sharePdf(url, fname);
}

function cap(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1) : s; }

// Short, credible one-line tasting note built from grade/origin/garden,
// used as a fallback when a product has no description.
export function teaBlurb(p) {
  if (!p) return "";
  const originAdj = {
    assam: "bright, full-bodied Assam",
    dooars: "smooth, coppery Dooars",
    darjeeling: "light, aromatic Darjeeling",
    terai: "brisk Terai",
    nilgiri: "clean, fragrant Nilgiri",
  }[(p.origin || "").trim().toLowerCase()];
  const grade = (p.grade || "").trim();
  const gradeTail = {
    Dust: "quick-brewing and strong, ideal for strong-tea markets",
    PD: "strong with a colourful cup",
    BP: "brisk with good colour",
    BOP: "well-balanced and brisk",
    BOPSM: "smooth, with neat tippy leaf",
    OF: "clean leaf with a balanced cup",
    PF: "a brisk, everyday cup",
  }[grade];
  const subject = originAdj
    ? `${cap(originAdj)} CTC${grade ? ` ${grade}` : ""}`
    : (p.garden ? `Well-made CTC${grade ? ` ${grade}` : ""} from ${p.garden}` : `Clean, well-made CTC${grade ? ` ${grade}` : ""} tea`);
  return gradeTail ? `${subject} — ${gradeTail}.` : `${subject}.`;
}

export function relTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

export function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

export function fmtDateTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-IN", {
    day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
  });
}

export const ROLE_LABEL = {
  admin: "Administrator",
  manager: "Sales Manager",
  executive: "Sales Executive",
};

export const LANG_LABEL = {
  hinglish: "Hinglish",
  hindi: "हिन्दी",
  english: "English",
};

export function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

export function parsePrice(priceStr) {
  if (!priceStr) return null;
  const s = String(priceStr);
  const m = s.match(/[\d,]+(?:\.\d+)?/);
  if (!m) return null;
  const num = parseFloat(m[0].replace(/,/g, ""));
  if (isNaN(num)) return null;
  const currency = (s.match(/[₹$€£]/) || [""])[0];
  const unit = s.slice(m.index + m[0].length);
  return { num, currency, unit, raw: s };
}

export function gstInclusive(priceStr, pct = 5) {
  const p = parsePrice(priceStr);
  if (!p) return null;
  const total = p.num * (1 + pct / 100);
  const fmt = (n) => (Number.isInteger(n) ? String(n) : n.toFixed(2));
  return {
    ...p, pct, totalNum: total,
    baseLabel: `${p.currency}${fmt(p.num)}${p.unit}`,
    totalLabel: `${p.currency}${fmt(total)}${p.unit}`,
  };
}

export function addDaysISO(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}
